import shallow from 'zustand/shallow';
import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Pagination, ToggleGroup, tokens } from '@hotjar/hotsauce';
import { measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';
import { useLocation } from 'react-router-dom';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { useInitialization, useCrosstab, useFrustrationAndEngagementScore } from './hooks';
import {
  applyAndNotifySegmentSaved,
  closeSaveSegmentModal,
  deleteRecordings,
  deleteSegment,
  deselectAllRecordings,
  disableNotWatchedPopoverInfo,
  exportRecordings,
  hideExportBanner,
  openAndFocusAddFilter,
  onItemsPerPageSet,
  onLocationChange,
  onPageSet,
  onSortingChange,
  notifySegmentsUpdated,
  saveConfigColumnsVisibility,
  saveHotjarFilters,
  selectAllRecordings,
  showRecording,
  showSaveSegmentModal,
  toggleSelectRecordings,
  toggleSelectPageRecordings,
  toggleRecordingsView,
  handleFilterChange,
  getOverviewRecordings,
  trackToggleClick,
} from './utils';
import { trackRecordingListViewed } from '../RecordingsPage/utils/getPlaybacksUtils';
import { RecordingsListActions } from '@hotjar/webapp/features/Recordings/RecordingsListActions/RecordingsListActions';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { RecordingsCount } from '../../components/common/RecordingsCount';
import { NpsTrademarkMessage } from '../../components/common/NpsTrademarkMessage';
import { PageHeader } from '../RecordingsList/PageHeader';
import { RecordingsList } from '../RecordingsList';
import { IpBlockingInfo } from '../IpBlockingInfo/IpBlockingInfo';
import {
  SectionContent,
  RecordingStateWrapper,
  CenterAlignedContainer,
  RecordingsPageContainer,
  RecordingsListContainer,
  HorizontalContainer,
  SegmentSidebarContainer,
  Spacer,
  EmptyStateWrapper,
} from './styledElements';
import { PendingInstallationEmptyState } from '@hotjar/webapp/features/Activation/EmptyStates';
import {
  FeatureEmptyState,
  EmptyStateKind,
} from '@hotjar/webapp/features/Activation/FeatureEmptyState';
import { RecordingsGuide } from '@hotjar/webapp/features/Activation/SpaceOverview/components/GuideCards';
import { RecordingFilterChain } from '@hotjar/webapp/features/Recordings/RecordingFilterChain';
import { SegmentEditCreate } from '@hotjar/webapp/features/Segments/SegmentEditCreate';
import { DismissibleBanner } from '@hotjar/webapp/features/Playbacks/DismissibleBanner';
import { SegmentSidebar } from '@hotjar/webapp/features/Segments/SegmentSidebar';
import { upsell as openUpsellModal } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';
import { useCurrentUser } from '@hotjar/user';
import { useRecordingsListStore } from '../../store/list';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import type { DismissibleBannerProps } from '@hotjar/webapp/features/Playbacks/DismissibleBanner';
import type { Site } from '@hotjar/site';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import type { Recording } from '../../store/list/types';
import {
  convertFromQueryToNewUiFormat,
  getDefaultFilterInUiFormat,
  GlobalFilter,
  QueryFormatFilter,
} from '@hotjar/global-filters';
import { TrafficUpsellBanner, TRAFFIC_UPSELL_BANNER } from '@hotjar/upsell';
import { RecordingsOverview } from '../RecordingsOverview';
import { useRecommendedWidgets } from './hooks/useRecommendedWidgets';
import { useExperimentVisibilityForTargetedCustomers } from './hooks/useExperimentVisibilityForTargetedCustomers';
import { WatchedRecordingsFilter, WatchedRecordingsFilterHandle } from './WatchedRecordingsFilter';
import { RelevanceScoreFilter } from './RelevanceScoreFilter';

const LocalFiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x1};
`;

// FIXME: featureName should be renamed to referrer, and should be of type KnownReferrer
const showUpsellModal = (currentSite: Site, featureName: string) => {
  openUpsellModal({
    site: currentSite,
    referrer: featureName as KnownReferrer, // FIXME: remove type assertion
  }).then(noop, noop);
};

export const RecordingsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const watchedRecordingsRef = useRef<WatchedRecordingsFilterHandle>(null);
  const hasIpBlockingManagePermission = usePermission('org-ignored-ips-manage');
  const location = useLocation();
  const [filterUiFormat, setFilterUiFormat] = useState<GlobalFilter>(getDefaultFilterInUiFormat());
  const { data: user } = useCurrentUser();

  useInitialization(useRecordingsListStore);
  useCrosstab(useRecordingsListStore);
  usePageTitle(`${t('common:recordings.list.page-title')} - Hotjar`);

  useEffect(() => {
    // Perf: Mark recordings list initialization timings
    measureAndLog({ name: MARKS.RECORDINGS_LIST_INITIALIZED, startMark: MARKS.APP_RENDERED });
  }, []);

  // Overview experiment
  // Check the experiment prerequisites and variant
  const { isOverviewExperimentEnabled, inExperiment } =
    useRecommendedWidgets(useRecordingsListStore);
  useEffect(() => {
    // Send tracking event for the experiment
    if (inExperiment) {
      const eventName = isOverviewExperimentEnabled
        ? 'recs_recommended_widgets_variant1'
        : 'recs_recommended_widgets_control';
      event(eventName);
    }
  }, [inExperiment, isOverviewExperimentEnabled]);

  useEffect(() => {
    // Fetch widgets data only once if the experiment is enabled.
    if (isOverviewExperimentEnabled) {
      getOverviewRecordings(useRecordingsListStore);
    }
  }, [isOverviewExperimentEnabled]);
  // Get info about the current view
  const { shouldDisplayOverview, resetFilterInteraction, enableFilterInteraction } =
    useExperimentVisibilityForTargetedCustomers(isOverviewExperimentEnabled);

  useEffect(() => {
    onLocationChange(useRecordingsListStore, enableFilterInteraction);
    // For the overview experiment, we need to pass down the enableFilterInteraction but we don't want to trigger the effect everytime it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Not reactive
  const { setAddFilterTippyInstance, setColumns } = useRecordingsListStore.getState();

  // Must be reactive
  const {
    columns,
    commentsByRecordingId,
    currentPage,
    currentSegment,
    editedSegment,
    exportBannerMessage,
    exportBannerKind,
    hotjarFilters,
    inTeaserMode,
    isEmptyStateCardVisible: isDataPending,
    isTrackingCodePromptVisible: isInstallationPending,
    itemsPerPage,
    jsonFilters,
    labelsByRecordingId,
    loaded,
    recordings,
    recordingsFetchedWithV3Api,
    searching: isLoading,
    segments,
    showActions,
    showExportBanner,
    showNotWatchedPopoverInfo,
    site: currentSite,
    totalHits: totalCount,
    totalRecordingsCount,
  } = useRecordingsListStore(
    (state) => ({
      columns: state.columns,
      commentsByRecordingId: state.commentsByRecordingId,
      currentPage: state.currentPage,
      currentSegment: state.currentSegment,
      editedSegment: state.editedSegment,
      exportBannerKind: state.exportBannerKind,
      exportBannerMessage: state.exportBannerMessage,
      hotjarFilters: state.hotjarFilters,
      inTeaserMode: state.inTeaserMode,
      isEmptyStateCardVisible: state.isEmptyStateCardVisible,
      isTrackingCodePromptVisible: state.isTrackingCodePromptVisible,
      itemsPerPage: state.itemsPerPage,
      jsonFilters: state.jsonFilters,
      labelsByRecordingId: state.labelsByRecordingId,
      loaded: state.loaded,
      recordings: state.recordings,
      recordingsFetchedWithV3Api: state.recordingsFetchedWithV3Api,
      searching: state.searching,
      segments: state.segments,
      showActions: state.showActions,
      showExportBanner: state.showExportBanner,
      showNotWatchedPopoverInfo: state.showNotWatchedPopoverInfo,
      site: state.site,
      totalHits: state.totalHits,
      totalRecordingsCount: state.totalRecordingsCount,
    }),
    shallow,
  );

  const { inVariant1: inEmptyStatesExperiment } = useAccountExperiment(
    'atv_empty_states_2023_03',
    currentSite?.account,
    isInstallationPending(),
  );

  const shouldHideSidebar = inEmptyStatesExperiment && isInstallationPending();

  // Frustration & Engagement score experiment
  const { isFrustrationAndEngagementScoreExperimentEnabled } =
    useFrustrationAndEngagementScore(useRecordingsListStore);

  useEffect(() => {
    if (!isFrustrationAndEngagementScoreExperimentEnabled || !recordingsFetchedWithV3Api) {
      // Avoid re-setting if column is not there
      if (!columns.frustration.showInPanel && !columns.engagement.showInPanel) {
        return;
      }

      setColumns({
        ...columns,
        frustration: { ...columns.frustration, showInPanel: false },
        engagement: { ...columns.engagement, showInPanel: false },
      });
      return;
    }

    // Avoid re-setting if column is already there
    if (columns.frustration.showInPanel || columns.engagement.showInPanel) {
      return;
    }

    setColumns({
      ...columns,
      frustration: { ...columns.frustration, showInPanel: true },
      engagement: { ...columns.engagement, showInPanel: true },
    });
  }, [
    columns,
    isFrustrationAndEngagementScoreExperimentEnabled,
    recordingsFetchedWithV3Api,
    setColumns,
  ]);

  useEffect(() => {
    if (jsonFilters) {
      setFilterUiFormat(convertFromQueryToNewUiFormat(jsonFilters));
    }
  }, [jsonFilters]);

  // @TODO Find a better way. e.g. Make one variable
  const [showPagination, showIpBlockingInfo] = Array(2).fill(!inTeaserMode() && totalCount > 0);
  const isSegmentEditCreateVisible = currentSite && editedSegment;

  const segmentEditCreate = isSegmentEditCreateVisible ? (
    <SegmentEditCreate
      isOpen={true}
      onClose={() => closeSaveSegmentModal(useRecordingsListStore)}
      segment={editedSegment}
      currentSite={currentSite}
      onSegmentChange={() => notifySegmentsUpdated(useRecordingsListStore)}
      onUpsellRequest={(featureName) => showUpsellModal(currentSite, featureName)}
      allowModeSelection={false}
      // FIXME [DXFE]: Fix css prop incompatibility
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      css={{ marginLeft: '10px' }}
    ></SegmentEditCreate>
  ) : null;

  const recordingsListActions = showActions() ? (
    <HorizontalContainer>
      <CenterAlignedContainer>
        <RecordingsCount count={totalCount} />
      </CenterAlignedContainer>
      <RecordingsListActions
        onExportList={(format) => exportRecordings(useRecordingsListStore, format)}
        onColumnsUpdated={(updatedColumns) =>
          saveConfigColumnsVisibility(useRecordingsListStore, updatedColumns)
        }
        listColumns={columns}
      />
    </HorizontalContainer>
  ) : null;

  const pagination = showPagination ? (
    <HorizontalContainer>
      <RecordingsCount count={totalCount} />
      <Pagination
        currentPage={currentPage}
        pageSize={itemsPerPage}
        totalCount={totalCount}
        onPageChange={(newPageNumber) => onPageSet(useRecordingsListStore, newPageNumber)}
        onPageSizeChange={(pageSize) => onItemsPerPageSet(useRecordingsListStore, pageSize)}
        itemsPerPageLabel={t('common:recordings.list.pagination-label')}
      />
    </HorizontalContainer>
  ) : null;

  const getRecordingsListView = () => (
    <>
      {recordingsListActions}
      <RecordingsList
        recordings={recordings}
        onRecordingSelectionChange={(recording: Recording) =>
          toggleSelectRecordings(useRecordingsListStore, [recording], true)
        }
        onPageSelectionChange={() => toggleSelectPageRecordings(useRecordingsListStore)}
        onPageDeletionCancel={() => deselectAllRecordings(useRecordingsListStore)}
        onSelectedRecordingsDelete={() => deleteRecordings(useRecordingsListStore)}
        onSelectAllRecordingsAcrossPages={() => selectAllRecordings(useRecordingsListStore)}
        onPlay={(recording) => showRecording(useRecordingsListStore, recording)}
        isLoading={isLoading}
        pageSize={itemsPerPage}
        currentPage={currentPage}
        onSort={(by, reverse) => onSortingChange(useRecordingsListStore, by, reverse)}
        listColumns={columns}
        commentsByRecordingId={commentsByRecordingId}
        labelsByRecordingId={labelsByRecordingId}
        watchedHotjarFilter={hotjarFilters.watched}
        onEmptyStateButtonClick={() =>
          showUpsellModal(
            currentSite as Site,
            'recordings.continuous-capture.increase-daily-limit-filtering-no-matches',
          )
        }
        onFiltersChangePromptClick={() => {
          watchedRecordingsRef?.current?.focusWatchedRecordingsSelect();
        }}
        isInstallationPending={isInstallationPending()}
        isDataPending={isDataPending()}
        totalCount={totalCount}
        onRecordingsChange={() => trackRecordingListViewed(useRecordingsListStore)}
        jsonFilters={jsonFilters as QueryFormatFilter}
        shouldShowFrustrationAndEngagementColumns={
          isFrustrationAndEngagementScoreExperimentEnabled && recordingsFetchedWithV3Api
        }
        site={currentSite as Site}
      />
      {pagination}
      <IpBlockingInfo showIpBlockingInfo={showIpBlockingInfo && hasIpBlockingManagePermission} />
    </>
  );
  const getRecordingsOverviewView = () => (
    <RecordingsOverview store={useRecordingsListStore}></RecordingsOverview>
  );

  const recordingsView = shouldDisplayOverview
    ? getRecordingsOverviewView()
    : getRecordingsListView();

  return (
    <>
      <RecordingsPageContainer>
        {loaded && !shouldHideSidebar ? (
          <SegmentSidebarContainer>
            <SegmentSidebar
              site={currentSite as Site}
              segments={segments}
              currentSegmentId={currentSegment?.id as number}
              onFilterClick={() => openAndFocusAddFilter(useRecordingsListStore)}
              onDelete={(segment) => deleteSegment(useRecordingsListStore, segment)}
              onSegmentChange={() => notifySegmentsUpdated(useRecordingsListStore)}
              onUpsellRequest={(featureName) => showUpsellModal(currentSite as Site, featureName)}
            />
          </SegmentSidebarContainer>
        ) : null}
        <RecordingsListContainer noSidebar={shouldHideSidebar} isLoading={isLoading}>
          <DismissibleBanner
            kind={exportBannerKind as DismissibleBannerProps['kind']}
            showBanner={showExportBanner}
            message={exportBannerMessage as string}
            onDismiss={() => hideExportBanner(useRecordingsListStore)}
          />
          <TrafficUpsellBanner
            upsell={openUpsellModal}
            isHidden={showExportBanner}
            type={TRAFFIC_UPSELL_BANNER.RECORDINGS}
          />
          <Spacer />

          {!inEmptyStatesExperiment || !isInstallationPending() ? (
            <PageHeader
              /* @TODO: The type definition for PageHeader is based on SegmentDropdownMenuProps.
            It forces us to pass kind as undefined.
            If it can be optional, could be defined as so.
          */
              kind={undefined}
              segment={currentSegment ?? undefined}
              onDeleteSegment={(segment) => deleteSegment(useRecordingsListStore, segment)}
              onEditSlackForwarding={(segment) =>
                showSaveSegmentModal(useRecordingsListStore, segment)
              }
              onEditSegment={(segment) => showSaveSegmentModal(useRecordingsListStore, segment)}
              subtitle=""
            >
              {loaded && isOverviewExperimentEnabled ? (
                <ToggleGroup
                  selectedValue={`${shouldDisplayOverview}`}
                  onChange={() => {
                    toggleRecordingsView(
                      useRecordingsListStore,
                      shouldDisplayOverview,
                      enableFilterInteraction,
                      resetFilterInteraction,
                    );
                    currentSite &&
                      user &&
                      trackToggleClick(
                        currentSite,
                        user.id,
                        shouldDisplayOverview ? 'Recordings Overview' : 'Recordings List',
                      );
                  }}
                  description="Recordings view"
                >
                  <ToggleGroup.Toggle value="true" icon="table" key="overview">
                    {`${t('common:recordings.recordings-overview.toggle.explore')}`}
                  </ToggleGroup.Toggle>
                  <ToggleGroup.Toggle value="false" icon="list" key="list">
                    {`${t('common:recordings.recordings-overview.toggle.list')}`}
                  </ToggleGroup.Toggle>
                </ToggleGroup>
              ) : null}
            </PageHeader>
          ) : null}

          {segmentEditCreate}

          {/* @TODO Improvement
              The teaser mode depends on the:
                - installed state of the script
                - total recordings count
                - recording mode

              The total recordings count is changed by the getPlaybacks function.
              In case we have the script installed and the recording mode it's not off,
              the only way the teaser mode can change is when there are more than 0 recordings.
              Since we have to call getPlaybacks().to know if the actual recordings are 0,
              we have to hide the element until we are not in teaser mode.
          */}
          <SectionContent className={inTeaserMode() ? 'hide' : ''}>
            <RecordingsGuide
              triggerFiltersModal={() => openAndFocusAddFilter(useRecordingsListStore)}
              totalRecordingsCount={totalRecordingsCount}
            />

            <RecordingFilterChain
              currentFiltersInUiFormat={filterUiFormat}
              currentSegment={currentSegment ?? undefined}
              segments={segments}
              onChange={(filtersInUiFormat, optionalSegmentId) => {
                handleFilterChange(
                  useRecordingsListStore,
                  filtersInUiFormat,
                  isOverviewExperimentEnabled,
                  enableFilterInteraction,
                  optionalSegmentId,
                );
              }}
              onSegmentSaved={(segment) =>
                applyAndNotifySegmentSaved(useRecordingsListStore, segment)
              }
              onUpsellRequest={(featureName) => showUpsellModal(currentSite as Site, featureName)}
              setAddFilterTippyInstance={setAddFilterTippyInstance}
            />

            {!shouldDisplayOverview ? (
              <LocalFiltersContainer>
                <WatchedRecordingsFilter
                  ref={watchedRecordingsRef}
                  onHotjarFiltersUpdated={(hotjarFilters) =>
                    saveHotjarFilters(useRecordingsListStore, hotjarFilters)
                  }
                  hotjarFilters={hotjarFilters}
                  showNotWatchedPopoverInfo={showNotWatchedPopoverInfo}
                  onNotWatchedPopoverInfoClosed={() =>
                    disableNotWatchedPopoverInfo(useRecordingsListStore)
                  }
                />
                <RelevanceScoreFilter
                  currentValue={hotjarFilters.relevanceScore}
                  hotjarFilters={hotjarFilters}
                  onHotjarFiltersUpdated={(hotjarFilters) =>
                    saveHotjarFilters(useRecordingsListStore, hotjarFilters)
                  }
                />
              </LocalFiltersContainer>
            ) : null}
          </SectionContent>
          <SectionContent>
            {isInstallationPending() ? (
              <>
                {inEmptyStatesExperiment ? (
                  <EmptyStateWrapper>
                    <FeatureEmptyState kind={EmptyStateKind.RECORDINGS} />
                  </EmptyStateWrapper>
                ) : (
                  <RecordingStateWrapper>
                    <PendingInstallationEmptyState />
                  </RecordingStateWrapper>
                )}
              </>
            ) : (
              recordingsView
            )}
          </SectionContent>
          <NpsTrademarkMessage />
        </RecordingsListContainer>
      </RecordingsPageContainer>
    </>
  );
};
